import {
  Button,
  Collapse,
  Descriptions,
  Dropdown,
  Popconfirm,
  Segmented,
  Skeleton,
  Space,
  Table,
  Tabs,
} from "antd";
import { useNavigate, useParams, useRouteLoaderData } from "react-router";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextFilled,
  MoreOutlined,
} from "@ant-design/icons";
import BackTitle from "../common/BackTitle";
import { api } from "../common/api";
import { useEffect, useState } from "react";
import { date2locale, downloadFile } from "../common/common";

const posRequestColumns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    width: 50,
  },
];

const PosRequests = ({ workSiteId }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.posRequest
      .list({ pageSize: 999999 })
      .then(setData)
      .finally(() => setLoading(false));
  }, [workSiteId]);
  return (
    <Table
      rowKey="id"
      dataSource={data.data}
      loading={loading}
      tableLayout={"fixed"}
      column={posRequestColumns}
    />
  );
};

const Pos = () => {
  const [selectedSegment, setSelectedSegment] = useState("workers");
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (id) {
      setLoading(true);
      api.pos
        .list({ workSite: id })
        .then((data) => setData(data.data?.[0] ?? {}))
        .finally(() => setLoading(false));
    }
  }, [id]);

  return (
    <Skeleton loading={loading} active>
      <div style={{ display: "flex", flexDirection: "column", rowGap: 32 }}>
        <div>
          <Button onClick={() => navigate("pos/new")}>Nuovo POS</Button>
        </div>
        <Descriptions
          layout="vertical"
          title="Informazioni generali"
          items={[
            { key: "id", label: "ID", children: data.id },
            {
              key: "issueDate",
              label: "Data di emissione",
              children: data.issueDate && date2locale(data.issueDate),
            },
            { key: "issuedBy", label: "Emesso da", children: data.issuedBy?.fullName },
            { key: "isVerified", label: "Verificato", children: data.isVerified ? "Si" : "No" },
            {
              key: "doc",
              label: "Documento",
              children: (
                <Button
                  size="small"
                  onClick={() => downloadFile("/pos/" + data.id + "/file")}
                  icon={<DownloadOutlined />}
                ></Button>
              ),
            },
            {
              key: "PSC",
              label: "PSC",
              children: <Button size="small" icon={<DownloadOutlined />}></Button>,
            },
          ]}
        />
        <Segmented
          style={{ alignSelf: "flex-start", marginTop: 32 }}
          options={[
            { label: "Lavoratori autorizzati", value: "workers" },
            { label: "Veicoli autorizzati", value: "vehicles" },
          ]}
          value={selectedSegment}
          onChange={setSelectedSegment}
        />
        <div style={{ marginBottom: 48 }}>
          {
            [
              {
                key: "workers",
                children: (
                  <Table
                    columns={[
                      {
                        key: "id",
                        title: "ID",
                        dataIndex: "id",
                      },
                      {
                        key: "fullName",
                        title: "Nominativo",
                        dataIndex: "fullName",
                      },
                      {
                        key: "mansion",
                        title: "Mansione",
                        dataIndex: ["mansion", "name"],
                      },
                    ]}
                    scroll={{ y: 240 }}
                    size="small"
                    pagination={false}
                    dataSource={data.employees ?? []}
                  />
                ),
              },
              {
                key: "vehicles",
                children: (
                  <Table
                    columns={[
                      {
                        key: "plate",
                        title: "Targa/Matricola",
                        dataIndex: "plate",
                      },
                      {
                        key: "description",
                        title: "Modello",
                        dataIndex: "description",
                      },
                      {
                        key: "vehicleType",
                        title: "Categoria",
                        dataIndex: ["type", "name"],
                      },
                    ]}
                    scroll={{ y: 240 }}
                    size="small"
                    pagination={false}
                    dataSource={data.vehicles ?? []}
                  />
                ),
              },
            ].filter((x) => x.key === selectedSegment)[0]?.children
          }
        </div>
      </div>
    </Skeleton>
  );
};

export default function () {
  const navigate = useNavigate();
  const data = useRouteLoaderData("workSite");
  const { id } = useParams();
  const [posView, setPosView] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.workSite
      .getPosView(id)
      .then(setPosView)
      .finally(() => setLoading(false));
  }, [id]);

  const items = [
    {
      key: "description",
      label: "Descrizione",
      children: data.description,
    },
  ];

  const tabs = [
    {
      key: "doc",
      label: "Piano operativo di Sicurezza",
      children: <Pos workSiteId={id} />,
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <BackTitle
        backEnabled={false}
        title="Dettagli cantiere"
        secondary={`ID: ${id}`}
        extra={
          <Space size={4}>
            {!posView.hasOngoingPosRequest && (
              <Button
                icon={<FileTextFilled />}
                type="primary"
                onClick={() => navigate("posRequest")}
              >
                {posView.hasLatestPos ? "Richiedi revisione POS" : "Richiedi emissione POS"}
              </Button>
            )}
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "deleteWorkSite",
                    label: (
                      <Popconfirm
                        title="Conferma rimozione"
                        onConfirm={() => api.workSite.delete(id).then(() => navigate(".."))}
                        description="L'operazione è irreversibile. Procedendo, i dati verranno rimossi definitivamente."
                      >
                        Elimina
                      </Popconfirm>
                    ),
                    danger: true,
                    icon: <DeleteOutlined />,
                  },
                ].filter((x) => !!x),
              }}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        }
      />
      <Descriptions
        layout="vertical"
        title={
          <>
            Informazioni generali
            <Button type="link" icon={<EditOutlined />} onClick={() => navigate("edit")} />
          </>
        }
        column={3}
        colon={false}
        items={items}
      />
      <Tabs style={{ marginTop: 50 }} defaultActiveKey="posRequests" items={tabs} />
    </div>
  );
}
